import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeaturedBuild = () => {
  const [featuredPost, setFeaturedPost] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        // Get the featured post (or most recent as fallback)
        const res = await axios.get(`${API}/blog/featured`);
        if (res.data.post) {
          setFeaturedPost(res.data.post);
        }
      } catch (err) {
        console.error("Failed to fetch featured build:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  // Auto-rotate photos
  useEffect(() => {
    if (!featuredPost?.photos || featuredPost.photos.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentPhotoIndex(prev => (prev + 1) % featuredPost.photos.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [featuredPost]);

  const nextPhoto = () => {
    if (!featuredPost?.photos) return;
    setCurrentPhotoIndex(prev => (prev + 1) % featuredPost.photos.length);
  };

  const prevPhoto = () => {
    if (!featuredPost?.photos) return;
    setCurrentPhotoIndex(prev => (prev - 1 + featuredPost.photos.length) % featuredPost.photos.length);
  };

  // Don't render anything if no featured post
  if (loading || !featuredPost) return null;

  return (
    <section className="py-16 md:py-24 px-4 bg-[#0a0a0a]" data-testid="featured-build-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-[#3b82f6] text-xs tracking-[0.3em] uppercase font-body">Latest Build</span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-wider uppercase mt-3">
            Featured Work
          </h2>
        </motion.div>

        {/* Featured Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-white/10 overflow-hidden"
        >
          {/* Photo Side */}
          <div className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[450px] bg-zinc-900 overflow-hidden group">
            {featuredPost.photos?.length > 0 ? (
              <>
                {featuredPost.photos.map((photo, idx) => (
                  <motion.img
                    key={idx}
                    src={photo}
                    alt={`${featuredPost.title} - Photo ${idx + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: idx === currentPhotoIndex ? 1 : 0 }}
                    transition={{ duration: 0.5 }}
                  />
                ))}
                
                {/* Photo Navigation */}
                {featuredPost.photos.length > 1 && (
                  <>
                    <button
                      onClick={prevPhoto}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      aria-label="Previous photo"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={nextPhoto}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      aria-label="Next photo"
                    >
                      <ChevronRight size={20} />
                    </button>
                    
                    {/* Dots */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {featuredPost.photos.slice(0, 6).map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentPhotoIndex(idx)}
                          className={`w-2 h-2 rounded-full transition-colors cursor-pointer border-none ${
                            idx === currentPhotoIndex ? 'bg-white' : 'bg-white/30'
                          }`}
                          aria-label={`Go to photo ${idx + 1}`}
                        />
                      ))}
                      {featuredPost.photos.length > 6 && (
                        <span className="text-white/50 text-xs">+{featuredPost.photos.length - 6}</span>
                      )}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-700">
                No photos yet
              </div>
            )}
            
            {/* Service Badge */}
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-[#3b82f6] text-white text-xs font-heading tracking-wider uppercase">
              {featuredPost.service}
            </div>
          </div>

          {/* Content Side */}
          <div className="p-8 md:p-12 flex flex-col justify-center bg-[#0d0d0d]">
            <h3 className="font-heading text-2xl md:text-3xl lg:text-4xl tracking-wider uppercase mb-4 text-white">
              {featuredPost.title}
            </h3>
            
            <p className="text-lg md:text-xl text-[#3b82f6] font-body mb-4">
              {featuredPost.vehicle}
            </p>
            
            {featuredPost.description && (
              <p className="text-zinc-400 font-body leading-relaxed mb-6 line-clamp-4">
                {featuredPost.description}
              </p>
            )}
            
            {/* Tags */}
            {featuredPost.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {featuredPost.tags.slice(0, 5).map(tag => (
                  <span 
                    key={tag} 
                    className="px-3 py-1 text-xs uppercase tracking-wider font-body border border-white/10 text-zinc-400"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            
            {/* Stats & CTA */}
            <div className="flex items-center justify-between mt-auto pt-6 border-t border-white/10">
              <div className="flex items-center gap-2 text-zinc-500 text-sm">
                <Eye size={16} />
                <span>{featuredPost.views || 0} views</span>
              </div>
              
              <button
                onClick={() => navigate("/portfolio")}
                className="group flex items-center gap-2 bg-white text-black font-heading text-sm tracking-[0.15em] uppercase px-6 py-3 hover:bg-zinc-200 transition-colors cursor-pointer border-none"
                data-testid="view-portfolio-btn"
              >
                View All Builds
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export { FeaturedBuild };
